const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID as string;
const LINKEDIN_REDIRECT_URI = `${import.meta.env.VITE_API_BASE_URL}/auth/linkedin/callback`;

/**
 * Builds the LinkedIn OAuth authorization URL and stores a CSRF nonce in sessionStorage.
 * role and organizationCode are only used when creating a brand-new user on the backend.
 * For existing users (login flow) they are ignored — pass empty strings.
 * intent distinguishes login ("login") from signup ("signup") so the backend can route
 * error redirects back to the correct page.
 * connectToUserId — if provided (add-another-account flow), the backend will
 * automatically link the two accounts in the DB after LinkedIn authentication.
 */
export function buildLinkedInAuthUrl(
  role = "achiever",
  organizationCode = "",
  intent: "login" | "signup" = "signup",
  connectToUserId?: string,
  expertProgramExternalId?: string,
  createExpertProgram?: boolean,
  /** The referral link's ?ref= (referrer's user id); the callback attributes the signup with it. */
  referrerId?: string,
): string {
  const nonce = crypto.randomUUID();
  const statePayload: Record<string, string | boolean> = { nonce, role, organizationCode, intent };
  if (connectToUserId) statePayload.connectToUserId = connectToUserId;
  if (expertProgramExternalId) {
    statePayload.expertProgramExternalId = expertProgramExternalId;
    statePayload.expertProgramUrlRole = localStorage.getItem("program_join_role") || "achiever";
  }
  if (createExpertProgram) statePayload.createExpertProgram = true;
  if (referrerId) statePayload.ref = referrerId;
  const state = btoa(JSON.stringify(statePayload))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  sessionStorage.setItem("linkedin_oauth_nonce", nonce);
  sessionStorage.setItem("linkedin_oauth_intent", intent);
  const params = new URLSearchParams({
    response_type: "code",
    client_id: LINKEDIN_CLIENT_ID,
    redirect_uri: LINKEDIN_REDIRECT_URI,
    state,
    scope: "openid profile email",
  });
  return `https://www.linkedin.com/oauth/v2/authorization?${params.toString()}`;
}
